import React from 'react';
import { X, Brain, Lightbulb, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { formatRelativeTime } from '../../utils/formatters';

const AIRecommendationModal = ({ incident, isOpen, onClose }) => {
  if (!isOpen || !incident) return null;

  const getRecommendations = (incident) => {
    const baseRecommendations = {
      'Database': {
        analysis: 'AI analysis indicates recurring connection pool exhaustion during peak hours',
        confidence: 92,
        recommendations: [
          {
            priority: 'High',
            action: 'Increase connection pool size from 20 to 50 connections',
            rationale: 'Current pool size insufficient for peak load based on historical data',
            effort: 'Low',
            impact: 'High'
          },
          {
            priority: 'Medium',
            action: 'Implement connection pool monitoring alerts',
            rationale: 'Early detection will prevent future incidents',
            effort: 'Medium',
            impact: 'Medium'
          },
          {
            priority: 'Low',
            action: 'Review query optimization opportunities',
            rationale: 'Some queries are holding connections longer than necessary',
            effort: 'High',
            impact: 'Medium'
          }
        ],
        similarIncidents: 3,
        pattern: 'Peak hour database connection timeouts'
      },
      'API': {
        analysis: 'AI detected API gateway upstream service timeout pattern',
        confidence: 87,
        recommendations: [
          {
            priority: 'High',
            action: 'Increase upstream timeout from 30s to 60s',
            rationale: 'Payment service response times exceed current timeout during peak',
            effort: 'Low',
            impact: 'High'
          },
          {
            priority: 'Medium',
            action: 'Implement circuit breaker pattern',
            rationale: 'Prevent cascade failures when payment service is slow',
            effort: 'High',
            impact: 'High'
          },
          {
            priority: 'Low',
            action: 'Add retry logic with exponential backoff',
            rationale: 'Improve resilience for transient failures',
            effort: 'Medium',
            impact: 'Medium'
          }
        ],
        similarIncidents: 5,
        pattern: 'API gateway timeout during payment processing'
      },
      'Performance': {
        analysis: 'AI analysis reveals memory leak pattern in user session management',
        confidence: 94,
        recommendations: [
          {
            priority: 'Critical',
            action: 'Deploy memory leak fix in user service v2.1.3',
            rationale: 'Known memory leak in session cleanup logic identified',
            effort: 'Low',
            impact: 'High'
          },
          {
            priority: 'High',
            action: 'Restart affected pods to clear accumulated memory',
            rationale: 'Immediate relief while permanent fix is deployed',
            effort: 'Low',
            impact: 'Medium'
          },
          {
            priority: 'Medium',
            action: 'Implement memory usage alerts at 80% threshold',
            rationale: 'Early warning system for future memory issues',
            effort: 'Low',
            impact: 'Medium'
          }
        ],
        similarIncidents: 8,
        pattern: 'Memory usage gradual increase over 24-48 hours'
      },
      'Security': {
        analysis: 'AI detected SSL certificate expiration risk pattern',
        confidence: 89,
        recommendations: [
          {
            priority: 'High',
            action: 'Renew SSL certificate immediately',
            rationale: 'Certificate expires in 7 days, causing service disruption',
            effort: 'Low',
            impact: 'High'
          },
          {
            priority: 'Medium',
            action: 'Implement automated certificate renewal',
            rationale: 'Prevent future manual certificate management issues',
            effort: 'Medium',
            impact: 'High'
          },
          {
            priority: 'Low',
            action: 'Set up certificate expiration monitoring',
            rationale: 'Alert 30 days before expiration for all certificates',
            effort: 'Low',
            impact: 'Medium'
          }
        ],
        similarIncidents: 2,
        pattern: 'Certificate expiration notifications'
      },
      'Cost': {
        analysis: 'AI identified unusual S3 storage cost spike due to log retention',
        confidence: 85,
        recommendations: [
          {
            priority: 'Medium',
            action: 'Implement log lifecycle policy',
            rationale: 'Debug logs are not being cleaned up automatically',
            effort: 'Low',
            impact: 'High'
          },
          {
            priority: 'Low',
            action: 'Review S3 storage class optimization',
            rationale: 'Move infrequently accessed logs to cheaper storage tiers',
            effort: 'Medium',
            impact: 'Medium'
          },
          {
            priority: 'Low',
            action: 'Set up cost anomaly detection alerts',
            rationale: 'Early notification of unusual spending patterns',
            effort: 'Low',
            impact: 'Medium'
          }
        ],
        similarIncidents: 1,
        pattern: 'Unexpected storage cost increase'
      }
    };

    return baseRecommendations[incident.category] || baseRecommendations['Database'];
  };

  const aiData = getRecommendations(incident);

  const getPriorityIcon = (priority) => {
    if (priority === 'Critical') return <AlertTriangle className="h-4 w-4 text-red-600" />;
    if (priority === 'High') return <AlertTriangle className="h-4 w-4 text-orange-600" />;
    if (priority === 'Medium') return <Clock className="h-4 w-4 text-yellow-600" />;
    return <CheckCircle className="h-4 w-4 text-blue-600" />;
  };

  const getPriorityColor = (priority) => {
    if (priority === 'Critical') return 'bg-red-50 border-red-200';
    if (priority === 'High') return 'bg-orange-50 border-orange-200';
    if (priority === 'Medium') return 'bg-yellow-50 border-yellow-200';
    return 'bg-blue-50 border-blue-200';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Brain className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">AI Recommendations</h2>
              <p className="text-sm text-gray-600">Intelligent analysis for {incident.number}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* AI Analysis Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="h-5 w-5 text-green-600 mt-1" />
              <div className="flex-1">
                <h3 className="font-medium text-green-900 mb-2">AI Analysis Summary</h3>
                <p className="text-green-800 mb-3">{aiData.analysis}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-green-700">Confidence: {aiData.confidence}%</span>
                  <span className="text-green-700">Similar Incidents: {aiData.similarIncidents}</span>
                  <span className="text-green-700">Pattern: {aiData.pattern}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommended Actions</h3>
            <div className="space-y-4">
              {aiData.recommendations.map((rec, index) => (
                <div key={index} className={`border rounded-lg p-4 ${getPriorityColor(rec.priority)}`}>
                  <div className="flex items-start gap-3">
                    {getPriorityIcon(rec.priority)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-900">{rec.action}</span>
                        <span className={`px-2 py-1 text-xs rounded ${
                          rec.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                          rec.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                          rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {rec.priority}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-3">{rec.rationale}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>Effort: {rec.effort}</span>
                        <span>Impact: {rec.impact}</span>
                      </div>
                    </div>
                    <button className="px-3 py-1 bg-white border border-gray-300 rounded text-sm hover:bg-gray-50 transition-colors">
                      Apply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Related Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Pattern Analysis</h4>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Time Pattern:</span>
                  <span className="font-medium">Peak hours (2-4 PM)</span>
                </div>
                <div className="flex justify-between">
                  <span>Frequency:</span>
                  <span className="font-medium">3-4 times/week</span>
                </div>
                <div className="flex justify-between">
                  <span>Duration:</span>
                  <span className="font-medium">15-30 minutes</span>
                </div>
                <div className="flex justify-between">
                  <span>Recovery Time:</span>
                  <span className="font-medium">5-10 minutes</span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Impact Assessment</h4>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Users Affected:</span>
                  <span className="font-medium">~2,500 users</span>
                </div>
                <div className="flex justify-between">
                  <span>Revenue Impact:</span>
                  <span className="font-medium">$3,200/hour</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Level:</span>
                  <span className="font-medium">99.2% (Target: 99.9%)</span>
                </div>
                <div className="flex justify-between">
                  <span>Resolution Priority:</span>
                  <span className="font-medium text-orange-600">High</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            AI analysis generated {formatRelativeTime(incident.updated_at)} • Powered by {incident.assigned_to}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              Implement All High Priority
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIRecommendationModal;